<template>
  <div :class="bemFacets" class="projection-selector">
    <ui-model-provider v-slot="{ hasItem, updateValue }" :value.sync="model">
      <ul class="u-reset projection-selector__items">
        <li
          v-for="({ description, id, label, projectionIconId }, idx) in options"
          :key="id"
          class="projection-selector__item"
        >
          <tooltip-initiator
            :is-enabled="hasTooltip($t(description))"
            :label="description"
            v-bind="tooltipProps"
            @scroll="$event.toggle(false)"
          >
            <template #default="{ hasHover, toggle }">
              <component
                :is="uiComponent"
                :checked="isChecked(id, hasItem)"
                :disabled="isDisabled(id)"
                :facets="uiComponentFacets"
                :name="getUid(idx)"
                :value="id"
                @change="updateValue($event)"
                @mouseenter.native="hasHover ? null : toggle(true)"
                @mouseleave.native="hasHover ? null : toggle(false)"
                @touchstart.native="hasHover ? toggle(true) : null"
                @touchend.native="hasHover ? toggle(false) : null"
              >
                <template #icon="{ iconClassesBase, iconClassesChecked }">
                  <responsive-img
                    :class="iconClassesBase"
                    :src="getProjectionIcon(projectionIconId, 'base')"
                    :width="78"
                    alt=""
                    loading="lazy"
                  />
                  <responsive-img
                    :class="iconClassesChecked"
                    :src="getProjectionIcon(projectionIconId, 'active')"
                    :width="78"
                    alt=""
                    loading="lazy"
                  />
                </template>
                <template v-if="showLabel" #label>
                  <span>{{ $t(label) }}</span>
                </template>
              </component>
            </template>
          </tooltip-initiator>
          <span v-if="showNote(id, description)" class="projection-selector__note">
            {{ $t(description) }}
            <a
              :href="sourceLink"
              class="projection-selector__note-link"
              rel="noopener noreferrer"
              target="_blank"
            >
              {{ $t('wizard_step.projection.source_label') }}
            </a>
          </span>
        </li>
      </ul>
    </ui-model-provider>
  </div>
</template>

<script>
  import { mapActions, mapState } from 'vuex'
  import bemMixin from '@/assets/js/mixin/bem'
  import ariaMixin from '@/assets/js/mixin/aria'
  import UiCheckbox from '@/components/form/ui-checkbox/ui-checkbox'
  import UiRadio from '@/components/form/ui-radio/ui-radio'
  import UiModelProvider from '@/components/form/ui-model-provider/ui-model-provider'
  import { projectionIconList } from '@/assets/js/model/projection'
  import { getImage } from '@/assets/js/util/misc/image'
  import TooltipInitiator from '@/components/tooltip-initiator/tooltip-initiator'
  import { viewportWidthIds, viewportWidthList } from '@/assets/js/util/dom/viewport-width'
  import {
    selectionModeIds,
    selectionModeList,
  } from '@/components/projection-selector/selection-mode'
  import ResponsiveImg from '@/components/responsive-img/responsive-img'

  export default {
    components: { ResponsiveImg, TooltipInitiator, UiModelProvider, UiCheckbox, UiRadio },
    mixins: [bemMixin('projection-selector'), ariaMixin('projection-selector')],
    props: {
      value: {
        type: [Array, String],
        required: true,
      },
      options: {
        type: Array,
        required: true,
      },
      availableOptions: {
        type: Array,
        default() {
          return []
        },
      },
      showLabel: {
        type: Boolean,
        default: false,
      },
      selectionModeId: {
        type: String,
        required: true,
        validator(value) {
          return Object.values(selectionModeIds).includes(value)
        },
      },
    },
    computed: {
      ...mapState('viewport', { viewportWidth: 'width' }),
      model: {
        get() {
          return this.value
        },
        set(model) {
          this.$emit('update:value', model)
        },
      },
      isCheckbox() {
        const { CHECKBOX } = this.$options.selectionModeIds
        return this.selectionModeId === CHECKBOX
      },
      selectionMode() {
        return this.$options.selectionModeList.find(({ id }) => id === this.selectionModeId)
      },
      sourceLink() {
        return this.$t('wizard_step.projection.source_link')
      },
      tooltipProps() {
        const mdViewport = viewportWidthList.find(({ id }) => id === viewportWidthIds.MD)

        if (this.viewportWidth < mdViewport.minValue) {
          return this.selectionMode.tooltipProps.mobile
        }

        return this.selectionMode.tooltipProps.desktop
      },
      uiComponent() {
        return this.selectionMode.component
      },
      uiComponentFacets() {
        return ['wizard', ...this.selectionMode.componentFacets]
      },
    },
    methods: {
      ...mapActions('overlay', ['closeOverlay', 'openOverlay']),
      hasTooltip(description) {
        if (!description) {
          return false
        }

        switch (this.selectionMode.id) {
          case selectionModeIds.PROJECTION_TYPE:
            return true
          case selectionModeIds.PROJECTION_SHAPE:
            return false
          case selectionModeIds.PROJECTION:
            return false
        }

        return false
      },
      isChecked(id, hasItem) {
        return this.isCheckbox ? hasItem(id) : id === this.value
      },
      isDisabled(value) {
        const options = this.availableOptions.length ? this.availableOptions : this.options
        return !options.some(({ id }) => id === value)
      },
      showNote(id, description) {
        const isProjection = this.selectionMode.id === selectionModeIds.PROJECTION
        const hasDescription = !!this.$t(description)

        return isProjection && hasDescription && id === this.value
      },
      getProjectionIcon(iconId, type) {
        const icon = this.$options.projectionIconList.find(({ id }) => id === iconId)
        return icon[type]
      },
      getUid(id) {
        return this.ariaUid(null, this.isCheckbox ? id : null)
      },
    },
    getImage,
    projectionIconList,
    selectionModeIds,
    selectionModeList,
  }
</script>

<style lang="scss" src="./_projection-selector.scss" />
